import { Link, useParams } from "react-router-dom";
import {
  formatNormalized,
  formatUsd,
  confidenceLabel,
  statusLabel,
  modalityLabel,
} from "../lib/format";
import {
  CommunityBadge,
  ConfidenceBadge,
  ModalityBadge,
  QualityBadge,
  StatusBadge,
} from "../components/Badges";
import { STRENGTH_KEYS, STRENGTH_LABELS } from "../types";
import { useCompare } from "../components/CompareContext";
import {
  useCatalogData,
  useCatalogModel,
  useCatalogProvider,
} from "../components/CatalogDataContext";
import { ProviderIcon } from "../components/ProviderIcon";
import { MatrixHeat } from "../components/ui/MatrixHeat";
import { StatusDot } from "../components/ui/StatusDot";
import { MetricBand } from "../components/ui/MetricBand";

export function ModelDetailPage() {
  const { id } = useParams();
  const model = useCatalogModel(id);
  const provider = useCatalogProvider(model?.providerId);
  const { meta } = useCatalogData();
  const { toggle, isSelected, canAdd } = useCompare();

  if (!model) {
    return (
      <div className="empty panel">
        <div className="empty-icon">?</div>
        <strong>Model not found</strong>
        <Link to="/">Back to catalog</Link>
      </div>
    );
  }
  const selected = isSelected(model.id);
  const priceText = formatNormalized(model);
  const hasPrice = model.pricing.normalizedUsd != null && priceText !== "—";
  const jev =
    model.confidenceSource === "jev" || model.confidenceSource === "refresh";

  const confKind =
    model.pricing.confidence === "verified"
      ? "live"
      : model.pricing.confidence === "estimated"
        ? "estimated"
        : "unknown";

  return (
    <div>
      <p className="eyebrow">01 / MODEL</p>
      <div className="detail-header">
        <div>
          <p className="breadcrumb">
            <Link to="/">Catalog</Link>
            {provider && (
              <>
                {" "}
                / <Link to={`/providers/${provider.id}`}>{provider.name}</Link>
              </>
            )}
            {" / "}
            {model.name}
          </p>
          <h1 className="tl-title" style={{ fontSize: "clamp(1.55rem, 2.8vw, 2.2rem)" }}>
            {model.name}
            {provider ? <span className="ctx"> / {provider.name}</span> : null}
          </h1>
          <div className="pill-row" style={{ marginTop: "0.55rem" }}>
            <ModalityBadge value={model.modality} />
            <StatusBadge value={model.status} />
            <QualityBadge value={model.qualityRank} />
            <ConfidenceBadge value={model.pricing.confidence} jev={jev} />
            <CommunityBadge score={model.communityScore} />
            <StatusDot kind={confKind} />
            {model.qualityRank === "sota" ? <StatusDot kind="sota" /> : null}
          </div>
          <div className="price-hero">
            <span className="price-label">Normalized price</span>
            <span
              className="price-value"
              style={!hasPrice ? { color: "var(--faint)", opacity: 0.55 } : undefined}
            >
              {priceText}
            </span>
            <div className="price-raw">{model.pricing.raw}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn ghost sm"
            disabled={!selected && !canAdd}
            onClick={() => toggle(model.id)}
          >
            {selected ? "Remove from compare" : "Add to compare"}
          </button>
          <a className="btn sm" href={model.docsUrl} target="_blank" rel="noreferrer">
            Official docs →
          </a>
        </div>
      </div>

      <MetricBand
        items={[
          { label: "Normalized", value: priceText, tone: hasPrice ? "amber" : "default" },
          { label: "Status", value: statusLabel(model.status).toUpperCase() },
          {
            label: "Confidence",
            value: confidenceLabel(model.pricing.confidence).toUpperCase(),
            tone: model.pricing.confidence === "verified" ? "lime" : "default",
          },
          { label: "Verified", value: model.lastVerified },
        ]}
      />

      <div className="stack">
        <section className="panel panel-pad">
          <h2 className="section-title" style={{ marginTop: 0 }}>
            02 / Pricing
          </h2>
          <dl className="kv">
            <dt>Raw</dt>
            <dd>{model.pricing.raw}</dd>
            <dt>Normalized</dt>
            <dd className="mono">{priceText}</dd>
            <dt>Confidence</dt>
            <dd>
              {confidenceLabel(model.pricing.confidence)}
              {jev ? " · Jev override" : ""}
            </dd>
            <dt>Last verified</dt>
            <dd className="mono">{model.lastVerified}</dd>
            {meta.lastRefreshAt && (
              <>
                <dt>Catalog refresh</dt>
                <dd className="mono">{meta.lastRefreshAt}</dd>
              </>
            )}
          </dl>
          {model.pricing.tiers.length > 0 && (
            <div className="table-wrap" style={{ marginTop: "1.1rem" }}>
              <table className="catalog" style={{ minWidth: 0 }}>
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th className="col-price">Amount</th>
                    <th>Unit</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {model.pricing.tiers.map((t) => (
                    <tr key={t.label + t.unit}>
                      <td>{t.label}</td>
                      <td className="col-price mono">{formatUsd(t.amountUsd)}</td>
                      <td className="mono">{t.unit}</td>
                      <td className="muted">{t.notes ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className="panel panel-pad">
          <h2 className="section-title" style={{ marginTop: 0 }}>
            03 / Specs
          </h2>
          <dl className="kv">
            <dt>Provider</dt>
            <dd className="provider-inline">
              <ProviderIcon provider={provider} size={18} />
              {provider ? (
                <Link to={`/providers/${provider.id}`}>{provider.name}</Link>
              ) : (
                model.providerId
              )}
            </dd>
            <dt>Endpoint</dt>
            <dd className="mono">{model.endpointId ?? model.id}</dd>
            <dt>Modality</dt>
            <dd>{modalityLabel(model.modality)}</dd>
            <dt>Status</dt>
            <dd>{statusLabel(model.status)}</dd>
            {model.limits?.maxResolution && (
              <>
                <dt>Max resolution</dt>
                <dd>{model.limits.maxResolution}</dd>
              </>
            )}
            {model.limits?.maxDurationSec != null && (
              <>
                <dt>Max duration</dt>
                <dd>{model.limits.maxDurationSec}s</dd>
              </>
            )}
            {model.limits?.rateLimitNote && (
              <>
                <dt>Rate limits</dt>
                <dd>{model.limits.rateLimitNote}</dd>
              </>
            )}
            {model.aliases && model.aliases.length > 0 && (
              <>
                <dt>Aliases</dt>
                <dd>{model.aliases.join(", ")}</dd>
              </>
            )}
          </dl>
          <div className="caps" style={{ marginTop: "0.9rem" }}>
            {model.capabilities.map((c) => (
              <span className="cap" key={c} style={{ maxWidth: "none" }}>
                {c}
              </span>
            ))}
          </div>
          {model.notes && (
            <p className="muted" style={{ marginTop: "0.9rem" }}>
              {model.notes}
            </p>
          )}
        </section>
        <section className="panel panel-pad">
          <h2 className="section-title" style={{ marginTop: 0 }}>
            04 / Capability matrix
          </h2>
          <MatrixHeat
            cells={STRENGTH_KEYS.map((k) => {
              const on = Boolean(model.strengths?.[k]);
              return {
                id: k,
                label: STRENGTH_LABELS[k],
                on,
                level: on ? "high" : "low",
                valueLabel: on ? "Yes" : "—",
              };
            })}
          />
        </section>
        <section className="panel panel-pad">
          <h2 className="section-title" style={{ marginTop: 0 }}>
            05 / Community caveats
          </h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Sampled from public Reddit and DuckDuckGo X/search snippets + Jev.
            Not a scientific benchmark. Admin can also paste tweet texts via API.
          </p>
          {model.communityScore != null && (
            <p>
              Community score: <CommunityBadge score={model.communityScore} />{" "}
              <span className="muted">
                (n={model.caveatsSampleSize ?? 0}
                {model.caveatsUpdatedAt
                  ? ` · ${model.caveatsUpdatedAt.slice(0, 10)}`
                  : ""}
                )
              </span>
            </p>
          )}
          {model.caveats && model.caveats.length > 0 ? (
            <ul className="caveat-list">
              {model.caveats.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">No caveats scored yet — unlock admin and run Refresh caveats.</p>
          )}
        </section>
      </div>
    </div>
  );
}
